import { inject, injectable, named } from 'inversify';

import env from '../../../env';
import ConfigService from '../../ConfigService';
import type { Config, Content, Menu } from '../../../../types/config';
import { ucFirst } from '../../../utils/formatting';
import AccountService from '../AccountService';

import StreamIQApiService from './StreamIQApiService';
import type { ListGroupsResponse, ListsResponse } from './types';
import StreamIQAccountService from './StreamIQAccountService';
import StreamIQData from './StreamIQData';

// Pre-login fallback resources: no region is known until the user signs in
const DEFAULT_REGION_ID = 'f27c3e84-ddc9-4782-8f65-d667bac657a8'; // Play NXT region
const DEFAULT_DESCRIPTION = 'Welcome to PlayNXT';
const FALLBACK_BANNER_URL = 'https://cdn.vd-ott.eu/portal/acc/image/x2rjrnnt.png';
const DISABLE_SHARE = true;

@injectable()
export default class StreamIQConfigService extends ConfigService {
  constructor(
    @inject(StreamIQData) protected readonly data: StreamIQData,
    @inject(StreamIQApiService) protected readonly apiService: StreamIQApiService,
    @inject(AccountService) @named('STREAMIQ') protected readonly accountService: StreamIQAccountService,
  ) {
    super();
  }

  formatSourceLocation = () => {
    return 'pseudo'; // A value is needed to prevent resetting to default config when the config is re-fetched
  };

  loadConfig = async () => {
    const regionId = this.accountService.regionId || DEFAULT_REGION_ID;
    const { siteName, logo, highlightColor, backgroundColor } = await this.accountService.getRegionConfigVariables(regionId);

    return {
      id: 'streamiq',
      siteName: siteName || env.APP_NAME,
      description: DEFAULT_DESCRIPTION,
      siteId: 'streamiq',
      assets: {
        banner: logo || FALLBACK_BANNER_URL,
      },
      // populated after login
      content: [
        // these are here to enable fetching the shelves on init
        { type: 'continue_watching' },
        { type: 'favorites' },
      ],
      menu: [],
      integrations: {
        streamiq: {},
      },
      styling: {
        footerText: '',
        highlightColor,
        backgroundColor,
      },
      features: {
        favoritesList: 'favorites',
        recommendationsPlaylist: 'recommendations',
        continueWatchingList: 'continueWatching',
        searchPlaylist: 'search',
      },
      custom: {
        disableShare: DISABLE_SHARE,
      },
    } satisfies Config;
  };

  getMenuItems = async () => {
    const listGroups = await this.apiService.get<ListGroupsResponse>(`/layout-service/listGroups?regionId=${this.accountService.regionId}`);
    const filtered = listGroups.resources.filter((group) => group.alias !== 'home');

    const menuItems: Menu[] = filtered.map((group) => ({
      contentId: `listgroup_${group.id}`,
      type: 'media',
      label: ucFirst(group.name),
    }));

    // TV Guide based on available lineups
    const lineup = this.data.getLineup();
    if (lineup) {
      // position the TV Guide menu item at ordinal 0
      const tvGuideIndex = filtered.findIndex(({ ordinal }) => !ordinal || ordinal > 0);
      menuItems.splice(tvGuideIndex, 0, { contentId: `lineup_${lineup.id}`, type: 'playlist', label: lineup.name });
    }

    return menuItems;
  };

  getContentItems = async () => {
    const listGroups = await this.apiService.get<ListGroupsResponse>(`/layout-service/listGroups?regionId=${this.accountService.regionId}`);

    const homeListGroup = listGroups.resources.find((group) => group.alias === 'home');
    const homeLists = await this.apiService.get<ListsResponse>(`/layout-service/lists?listGroupId=${homeListGroup?.id}`);

    return homeLists.resources.map((list, index) => {
      // Continue watching is rendered from our own watch history (synced with the StreamIQ bookmarks),
      // so the bookmarks list is mapped to our shelf instead of a playlist shelf
      if (list.query.includes('session-service/bookmarks')) {
        return { type: 'continue_watching', title: list.name } as Content;
      }

      return {
        contentId: list.id,
        type: 'playlist',
        title: ucFirst(list.name),
        custom: index === 0 ? { layoutType: 'hero' } : list.customVariable ? JSON.parse(list.customVariable) : {},
      } as Content;
    });
  };
}
